import { SUBSCRIPTIONS_API_KEYS } from "../constants/MySubscriptions.constants";
import { RECURRENCE_MONTHS } from "./tenure.constants";

export const getDiscount = ({ monthlyPrice, planPrice, planTenure }) => {
  const totalMonthlyCost = monthlyPrice * planTenure;
  if (!totalMonthlyCost) return 0;
  return Math.round(((totalMonthlyCost - planPrice) / totalMonthlyCost) * 100);
};

const PLAN_NAME_SUFFIX_MAP = {
  monthly: "/month",
  quarterly: "/quarter",
  yearly: "/year",
};

export const getPlanByRecurrenceMonths = (
  featurePlans = [],
  recurrenceMonths
) =>
  featurePlans.find(
    (plan) =>
      Number(plan[SUBSCRIPTIONS_API_KEYS.recurrence_months]) ===
      Number(recurrenceMonths)
  );

export const getTenureDisplaySuffix = (tenurePlan) => {
  if (!tenurePlan) return "";

  const planName = tenurePlan[SUBSCRIPTIONS_API_KEYS.plan_name];
  const normalizedName = planName?.toLowerCase().trim();

  if (normalizedName && PLAN_NAME_SUFFIX_MAP[normalizedName]) {
    return PLAN_NAME_SUFFIX_MAP[normalizedName];
  }

  if (planName) {
    return `/${planName.toLowerCase().replace(/\s+/g, "-")}`;
  }

  const months =
    Number(tenurePlan[SUBSCRIPTIONS_API_KEYS.recurrence_months]) || 1;
  if (months === RECURRENCE_MONTHS.MONTHLY) return "/month";
  if (months === RECURRENCE_MONTHS.QUARTERLY) return "/quarter";
  if (months === RECURRENCE_MONTHS.YEARLY) return "/year";

  return `/${months}mo`;
};

export const getTenureOptionsFromFeaturePlans = (featurePlans = []) => {
  const byRecurrence = new Map();

  featurePlans.forEach((plan) => {
    const recurrenceMonths = Number(
      plan[SUBSCRIPTIONS_API_KEYS.recurrence_months]
    );
    const existing = byRecurrence.get(recurrenceMonths);

    if (
      !existing ||
      (plan[SUBSCRIPTIONS_API_KEYS.is_popular] &&
        !existing[SUBSCRIPTIONS_API_KEYS.is_popular])
    ) {
      byRecurrence.set(recurrenceMonths, plan);
    }
  });

  return Array.from(byRecurrence.entries())
    .sort(([a], [b]) => a - b)
    .map(([value, plan]) => ({
      id: value,
      value,
      label:
        plan[SUBSCRIPTIONS_API_KEYS.plan_name] ||
        `${value} month${value > 1 ? "s" : ""}`,
      plan,
    }));
};

export const getTenureOptionsFromSubscriptionPlans = (
  subscriptionPlans = [],
  { consumableOnly = false } = {}
) => {
  const byRecurrence = new Map();

  subscriptionPlans.forEach((feature) => {
    if (consumableOnly && feature[SUBSCRIPTIONS_API_KEYS.is_access_type]) {
      return;
    }

    const featurePlans = feature[SUBSCRIPTIONS_API_KEYS.plans] ?? [];

    featurePlans.forEach((plan) => {
      const recurrenceMonths = Number(
        plan[SUBSCRIPTIONS_API_KEYS.recurrence_months]
      );
      const existing = byRecurrence.get(recurrenceMonths);

      if (
        !existing ||
        (plan[SUBSCRIPTIONS_API_KEYS.is_popular] &&
          !existing[SUBSCRIPTIONS_API_KEYS.is_popular])
      ) {
        byRecurrence.set(recurrenceMonths, plan);
      }
    });
  });

  return Array.from(byRecurrence.entries())
    .sort(([a], [b]) => a - b)
    .map(([value, plan]) => ({
      id: value,
      value,
      label:
        plan[SUBSCRIPTIONS_API_KEYS.plan_name] ||
        `${value} month${value > 1 ? "s" : ""}`,
      plan,
    }));
};

export const getTenureOptionsWithDiscounts = (featurePlans = []) => {
  const options = getTenureOptionsFromFeaturePlans(featurePlans);

  if (!options.length) return [];

  const baseOption = options[0];
  const basePerMonth =
    (baseOption.plan[SUBSCRIPTIONS_API_KEYS.default_price] ?? 0) /
    baseOption.value;

  return options.map((option) => {
    const planPrice = option.plan[SUBSCRIPTIONS_API_KEYS.default_price] ?? 0;
    const discount =
      option.value === baseOption.value
        ? undefined
        : getDiscount({
            monthlyPrice: basePerMonth,
            planPrice,
            planTenure: option.value,
          });

    return {
      ...option,
      info: discount ? `Save ${discount}%` : undefined,
    };
  });
};

export const resolveSelectedTenure = (currentTenure, tenureOptions = []) => {
  if (!tenureOptions.length) return currentTenure;

  const isValid = tenureOptions.some(
    (option) => Number(option.value) === Number(currentTenure)
  );

  return isValid ? currentTenure : tenureOptions[0].value;
};

export const getBasePerMonthPrice = (featurePlans = []) => {
  const [baseOption] = getTenureOptionsFromFeaturePlans(featurePlans);

  if (!baseOption) return 0;

  return (
    (baseOption.plan[SUBSCRIPTIONS_API_KEYS.default_price] ?? 0) /
    baseOption.value
  );
};
